import React, { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useOptionalWorkspace } from '../lib/workspace';
import { useQuery } from '@tanstack/react-query';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import {
  closeAlertBulkScoped,
  convertAggregateAlerts,
  convertAlertBulk,
  getAlertPreferences,
  listAggregateAlerts,
  listAlerts,
  listMyWorkspaces,
  snoozeAlertBulkScoped,
  type AlertCategory,
  type AlertListItem,
  type AlertSeverity,
  type AlertStatus,
} from '../lib/api';
import {
  Alert,
  Button,
  CategoryIcon,
  Checkbox,
  PageHeader,
  Select,
  SeverityBadge,
  Spinner,
  WorkspaceTag,
  alertCategoryLabel,
} from '@aigovops/design-system';
import { fmtDate } from '../lib/format';
import { AlertsEmpty } from './AlertsEmpty';
import { SnoozeDialog } from '../components/alerts/SnoozeDialog';
import { ConfirmDialog } from '../components/alerts/ConfirmDialog';
import { AggregateRollup, buildRollups, refCount } from '../components/alerts/AggregateRollup';
import { roster } from '../data/editors';

// PR-P3-2: URL-persisted Alerts filters — same "predicate + typed union +
// replace: true + default-clears-param" recipe as Register.tsx.
const CATEGORY_VALUES: AlertCategory[] = ['legislation', 'litigation', 'framework', 'breach', 'cert'];
const SEVERITY_VALUES: AlertSeverity[] = ['critical', 'high', 'elevated', 'moderate', 'info'];
// UC-4 R2 sub-nav status options. 'snoozed' AND 'actioned' are both
// intentionally omitted from the real AlertStatus set a user can pick —
// AlertsService never writes either value (snooze lives entirely in the
// per-user `alert_user_state` overlay; nothing ever sets
// alerts.status='actioned'). The "Actioned" hero tile instead maps to
// the synthetic `done` bucket below (converted OR closed), which IS
// reachable. Filed as a P3-2 follow-up: a dedicated "snoozed for me"
// list needs a new backend query.
const STATUS_VALUES: AlertStatus[] = ['open', 'converted', 'closed'];
type StatusFilterValue = AlertStatus | 'all' | 'done';

function isCategory(v: string | null): v is AlertCategory {
  return v !== null && (CATEGORY_VALUES as string[]).includes(v);
}
function isSeverity(v: string | null): v is AlertSeverity {
  return v !== null && (SEVERITY_VALUES as string[]).includes(v);
}
function isStatus(v: string | null): v is AlertStatus {
  return v !== null && (STATUS_VALUES as string[]).includes(v);
}

const DEADLINE_WINDOW_DAYS = 30;

export function AlertsInbox() {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;
  const prefix = slug ? `/w/${slug}/app` : '/app'; // scope-check-ignore: legacy fallback
  const canConvert =
    workspaceCtx?.role === 'owner' || workspaceCtx?.role === 'admin' || workspaceCtx?.role === 'editor';

  const [searchParams, setSearchParams] = useSearchParams();

  // Consultant aggregate mode (PR-P3-4 · nav-ia-v2 §7.6 + §9.6). A URL
  // param rather than a separate route: the aggregate inbox is the same
  // surface with a wider row set, and every filter below already
  // round-trips through the query string, so `?view=aggregate` is
  // shareable and back-button-correct for free. The ambient `:slug`
  // stays in the path because the API is still reached through
  // `workspaceContext()` — it authenticates the request; it does not
  // scope the result.
  const aggregate = searchParams.get('view') === 'aggregate';

  // Membership list drives whether aggregate is offered at all. Asked
  // of the server rather than inferred from the rows that come back:
  // a consultant whose second client simply has no alerts this week is
  // still a consultant, and the toggle must not disappear on them.
  const myWorkspacesQ = useQuery({
    queryKey: queryKeys.myWorkspaces(),
    queryFn: () => listMyWorkspaces(),
  });
  const myWorkspaces = {
    data: myWorkspacesQ.data ?? null,
    error: myWorkspacesQ.error?.message ?? null,
    loading: myWorkspacesQ.isLoading,
    refetch: myWorkspacesQ.refetch,
  };
  const teamCount = useMemo(
    () => (myWorkspaces.data?.workspaces ?? []).filter((w) => w.kind === 'team').length,
    [myWorkspaces.data]
  );
  const canAggregate = teamCount >= 2 && workspaceCtx?.role !== 'assessor';

  // Convert rights are PER CLIENT. A consultant is routinely an editor
  // for two clients and a viewer for the third, so gating the aggregate
  // inbox's checkboxes on the ambient workspace's role would hide the
  // action entirely from someone parked in the one client they can't
  // write to — and offer it on rows they can't write to either. The map
  // is the same authority the server enforces per alert, surfaced early
  // so the limit is visible BEFORE the action, not reported after it.
  const convertRoleBySlug = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const w of myWorkspaces.data?.workspaces ?? []) {
      m.set(w.slug, w.role === 'owner' || w.role === 'admin' || w.role === 'editor');
    }
    return m;
  }, [myWorkspaces.data]);
  function canConvertRow(row: AlertListItem): boolean {
    if (!aggregate) return canConvert;
    return row.workspace_slug ? convertRoleBySlug.get(row.workspace_slug) ?? false : false;
  }
  // Gates the bulk-bar and the rollup's convert CTA in aggregate mode.
  const canConvertAnywhere = aggregate
    ? [...convertRoleBySlug.values()].some(Boolean)
    : canConvert;

  // Fetch the full active-or-recent set once and filter client-side —
  // same shape as Register.tsx. Alert volume per workspace is modest
  // (tens, not thousands), so one broad fetch is simpler than a
  // server round-trip per filter change.
  const alerts = useWorkspaceQuery(
    (s) => queryKeys.alerts(s, aggregate),
    (s) => aggregate
      ? listAggregateAlerts(s, { status: 'all', limit: 200 })
      : listAlerts(s, { status: 'all', limit: 200 }),
  );
  const prefs = useWorkspaceQuery(
    (s) => queryKeys.alertPreferences(s),
    (s) => getAlertPreferences(s),
  );

  const rawStatus = searchParams.get('status');
  const rawCategory = searchParams.get('category');
  const rawSeverity = searchParams.get('severity');
  const rawWorkspace = searchParams.get('client');
  const unreadOnly = searchParams.get('unread') === '1';
  const deadlineOnly = searchParams.get('deadline') === '30d';
  const window30d = searchParams.get('window') === '30d';
  const statusFilter: StatusFilterValue =
    rawStatus === 'done' ? 'done' : isStatus(rawStatus) ? rawStatus : window30d ? 'all' : 'open';
  const categoryFilter: AlertCategory | 'all' = isCategory(rawCategory) ? rawCategory : 'all';
  const severityFilter: AlertSeverity | 'all' = isSeverity(rawSeverity) ? rawSeverity : 'all';

  // Every filter change clears the selection. Without this, ticking
  // rows under "All clients" and then switching the Client filter left
  // a selection referring to rows that are no longer on screen — and
  // the sticky bar, the confirm dialog and the write all still operated
  // on them. That is a cross-tenant mis-write with every visible pixel
  // saying the other client's name, which is the single worst outcome
  // this surface can produce (ux-adversary Nadia Blocker).
  const setParam = (key: string, value: string | null) => {
    setSelected(new Set());
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (value === null || value === '') params.delete(key);
        else params.set(key, value);
        return params;
      },
      { replace: true }
    );
  };

  const listRef = useRef<HTMLUListElement>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [confirmKind, setConfirmKind] = useState<'convert' | 'dismiss' | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);
  const [priorSelection, setPriorSelection] = useState<Set<string> | null>(null);

  function openSnooze() {
    if (selected.size === 0) return;
    setBulkError(null);
    setBulkNotice(null);
    setConfirmKind(null);
    setSnoozeOpen(true);
  }
  function openConfirm(kind: 'convert' | 'dismiss') {
    if (selected.size === 0) return;
    setBulkError(null);
    setBulkNotice(null);
    setSnoozeOpen(false);
    setConfirmKind(kind);
  }

  const rows: AlertListItem[] = alerts.data?.alerts ?? [];
  const serverTotal = alerts.data?.total ?? 0;
  const isTruncated = rows.length < serverTotal;

  // Newest derived alert stands in for "this feed is live" — the
  // underlying legislation/case-law/breach research sources carry their
  // own editorial review dates; this is the freshest signal the Alerts
  // surface itself has to show per Contract C3.
  const feedUpdatedAt = useMemo(
    () => rows.reduce<string | null>((max, r) => (!max || r.created_at > max ? r.created_at : max), null),
    [rows]
  );

  // Scoped to status='open' — the default view — so a hero-tile count
  // matches what you actually see after clicking it (ux-adversary
  // Nadia/Marcus/Glance convergent finding: counts computed over a
  // wider row set than the click-through filter showed produced a
  // number that never reproduced).
  const openRows = useMemo(() => rows.filter((r) => r.status === 'open'), [rows]);
  const nowMs = Date.now();
  const heroCounts = useMemo(() => {
    const unread = openRows.filter((r) => !r.read_at).length;
    const critical = openRows.filter((r) => r.severity === 'critical').length;
    const deadlines = openRows.filter((r) => {
      if (!r.deadline_at) return false;
      const t = Date.parse(r.deadline_at);
      if (Number.isNaN(t)) return false;
      const days = (t - nowMs) / (24 * 60 * 60 * 1000);
      return days >= 0 && days <= DEADLINE_WINDOW_DAYS;
    }).length;
    const done = rows.filter((r) => r.status === 'converted' || r.status === 'closed').length;
    return { unread, critical, deadlines, done };
  }, [openRows, rows, nowMs]);

  // Distinct clients present in the current row set, for the "By client"
  // filter. Sorted by name so the list order doesn't shuffle as alerts
  // arrive.
  // Driven from memberships, not from the rows that happened to come
  // back — a client with no alerts this week is still a client, and the
  // dropdown must show it so "quiet" is distinguishable from "missing".
  const clients = useMemo(() => {
    const ws = myWorkspaces.data?.workspaces ?? [];
    return ws
      .filter((w) => w.kind === 'team')
      .map((w) => ({ slug: w.slug, name: w.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [myWorkspaces.data]);
  const clientFilter =
    rawWorkspace && clients.some((c) => c.slug === rawWorkspace) ? rawWorkspace : 'all';

  const filtered = useMemo(() => {
    let list = rows;
    if (clientFilter !== 'all') list = list.filter((r) => r.workspace_slug === clientFilter);
    if (statusFilter === 'done') list = list.filter((r) => r.status === 'converted' || r.status === 'closed');
    else if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter);
    if (categoryFilter !== 'all') list = list.filter((r) => r.category === categoryFilter);
    if (severityFilter !== 'all') list = list.filter((r) => r.severity === severityFilter);
    if (unreadOnly) list = list.filter((r) => !r.read_at);
    if (deadlineOnly) {
      list = list.filter((r) => {
        if (!r.deadline_at) return false;
        const t = Date.parse(r.deadline_at);
        if (Number.isNaN(t)) return false;
        const days = (t - nowMs) / (24 * 60 * 60 * 1000);
        return days >= 0 && days <= DEADLINE_WINDOW_DAYS;
      });
    }
    return [...list].sort((a, b) => {
      const order: Record<AlertSeverity, number> = { critical: 0, high: 1, elevated: 2, moderate: 3, info: 4 };
      const bySeverity = order[a.severity] - order[b.severity];
      if (bySeverity !== 0) return bySeverity;
      return Date.parse(b.created_at) - Date.parse(a.created_at);
    });
  }, [rows, clientFilter, statusFilter, categoryFilter, severityFilter, unreadOnly, deadlineOnly, nowMs]);

  // Which clients the current selection spans — drives the bulk-bar
  // count and, more importantly, the names in the convert confirmation.
  const selectedWorkspaceNames = useMemo(() => {
    const names = new Set<string>();
    for (const r of rows) {
      if (selected.has(r.alert_id) && r.workspace_name) names.add(r.workspace_name);
    }
    return [...names].sort();
  }, [rows, selected]);
  const selectedWorkspaceCount = selectedWorkspaceNames.length;

  // Rollups are built from the FILTERED set, not the raw one — a card
  // reading "affects 3 workspaces" while the list below it shows one
  // client (because "By client" is on) would be describing an inbox the
  // user isn't looking at.
  // Rollups from a status-agnostic set: a signal that hit three clients
  // but was already converted in one should still say "Affects 3" so
  // the card states the true blast radius. The status filter is the
  // one filter that hides a real client from the count.
  const rollupSource = useMemo(() => {
    let list = rows;
    if (clientFilter !== 'all') list = list.filter((r) => r.workspace_slug === clientFilter);
    if (categoryFilter !== 'all') list = list.filter((r) => r.category === categoryFilter);
    if (severityFilter !== 'all') list = list.filter((r) => r.severity === severityFilter);
    if (unreadOnly) list = list.filter((r) => !r.read_at);
    return list;
  }, [rows, clientFilter, categoryFilter, severityFilter, unreadOnly]);
  const rollups = useMemo(() => (aggregate ? buildRollups(rollupSource) : []), [aggregate, rollupSource]);

  const noFiltersActive =
    clientFilter === 'all' &&
    statusFilter === 'open' &&
    categoryFilter === 'all' &&
    severityFilter === 'all' &&
    !unreadOnly &&
    !deadlineOnly;

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  /**
   * Selected alerts paired with the workspace each one actually lives
   * in. Falls back to the ambient slug only when a row predates the
   * workspace-identity columns; in aggregate mode the row's own slug is
   * the only correct address.
   */
  function selectedRefs() {
    const bySlug = new Map(rows.map((r) => [r.alert_id, r.workspace_slug ?? slug ?? '']));
    return [...selected]
      .map((id) => ({ id, slug: bySlug.get(id) ?? '' }))
      .filter((r) => r.slug !== '');
  }

  /**
   * Cross-tenant convert. One server call that fans out per alert, each
   * checked against the caller's role in THAT alert's workspace — not
   * the client-side `Promise.allSettled` loop the single-workspace path
   * uses, which would authorize every write with the ambient session's
   * role in whichever workspace the URL happens to name.
   */
  async function runAggregateConvert(ids: string[]) {
    if (!slug || ids.length === 0) return;
    setBulkBusy(true);
    setBulkError(null);
    setBulkNotice(null);
    try {
      const res = await convertAggregateAlerts(slug, ids);
      const ok = res.converted.filter((r) => r.status === 'ok').length;
      const refused = res.converted.filter((r) => r.status === 'refused').length;
      const forbidden = res.skipped.filter((r) => r.reason === 'forbidden').length;
      const otherSkips = res.skipped.length - forbidden;
      if (refused > 0 || res.skipped.length > 0) {
        setBulkNotice(
          `${ok} of ${ids.length} converted.` +
            (refused ? ` ${refused} had no matching register items.` : '') +
            (forbidden
              ? ` ${forbidden} skipped — you don't have editor access in that client's workspace.`
              : '') +
            (otherSkips ? ` ${otherSkips} couldn't be found.` : '')
        );
      } else {
        setBulkNotice(`${ok} alert${ok === 1 ? '' : 's'} converted.`);
      }
      setConfirmKind(null);
      clearSelection();
      alerts.refetch();
      requestAnimationFrame(() => listRef.current?.focus());
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Failed to convert alerts.');
      alerts.refetch();
    } finally {
      setBulkBusy(false);
    }
  }

  async function runBulkConvert() {
    if (!slug) return;
    if (aggregate) return runAggregateConvert([...selected]);
    setBulkBusy(true);
    setBulkError(null);
    setBulkNotice(null);
    try {
      const ids = [...selected];
      const settled = await convertAlertBulk(slug, ids);
      const ok = settled.filter((r) => r.status === 'fulfilled');
      const failed = settled.filter((r) => r.status === 'rejected');
      const refused = ok.filter((r) => r.status === 'fulfilled' && r.value.status === 'refused');
      const converted = ok.length - refused.length;
      if (failed.length > 0 || refused.length > 0) {
        setBulkNotice(
          `${converted} of ${ids.length} converted.` +
            (refused.length ? ` ${refused.length} refused (no matching register items).` : '') +
            (failed.length ? ` ${failed.length} failed to reach the server — retry those.` : '')
        );
      }
      // Close the dialog BEFORE clearing selection — clearSelection()
      // unmounts the bulk-bar (and the button that opened this dialog);
      // if that happens first, <dialog>'s focus-restoration has nowhere
      // connected to return focus to and keyboard users land on <body>
      // (ux-adversary Accessibility Medium).
      setConfirmKind(null);
      clearSelection();
      alerts.refetch();
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Failed to convert alerts.');
    } finally {
      setBulkBusy(false);
    }
  }

  async function runBulkDismiss() {
    if (!slug) return;
    setBulkBusy(true);
    setBulkError(null);
    setBulkNotice(null);
    try {
      const settled = await closeAlertBulkScoped(selectedRefs());
      const forbidden = settled.filter(
        (r) => r.status === 'rejected' && r.reason instanceof Error && 'status' in r.reason && (r.reason as any).status === 403
      ).length;
      const otherFails = settled.filter((r) => r.status === 'rejected').length - forbidden;
      if (forbidden > 0 || otherFails > 0) {
        const ok = settled.length - forbidden - otherFails;
        setBulkNotice(
          `${ok} of ${settled.length} dismissed.` +
            (forbidden ? ` ${forbidden} skipped — you don't have editor access in that workspace.` : '') +
            (otherFails ? ` ${otherFails} failed — retry those.` : '')
        );
      }
      setConfirmKind(null);
      clearSelection();
      alerts.refetch();
      requestAnimationFrame(() => listRef.current?.focus());
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Failed to dismiss alerts.');
    } finally {
      setBulkBusy(false);
    }
  }

  async function runBulkSnooze(until: Date) {
    if (!slug) return;
    setBulkBusy(true);
    setBulkError(null);
    setBulkNotice(null);
    try {
      const settled = await snoozeAlertBulkScoped(selectedRefs(), until.toISOString());
      const forbidden = settled.filter(
        (r) => r.status === 'rejected' && r.reason instanceof Error && 'status' in r.reason && (r.reason as any).status === 403
      ).length;
      const otherFails = settled.filter((r) => r.status === 'rejected').length - forbidden;
      if (forbidden > 0 || otherFails > 0) {
        const ok = settled.length - forbidden - otherFails;
        setBulkNotice(
          `${ok} of ${settled.length} snoozed.` +
            (forbidden ? ` ${forbidden} skipped — you don't have editor access in that workspace.` : '')  +
            (otherFails ? ` ${otherFails} failed — retry those.` : '')
        );
      }
      setSnoozeOpen(false);
      clearSelection();
      alerts.refetch();
      requestAnimationFrame(() => listRef.current?.focus());
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Failed to snooze alerts.');
    } finally {
      setBulkBusy(false);
    }
  }

  // Only the FIRST load blocks the page behind a spinner. Refetches
  // after an action (convert/dismiss/snooze) keep the existing rows on
  // screen — gating on `loading` unconditionally unmounted the whole
  // page (losing scroll position + the bulk-bar) on every single
  // triage action (ux-adversary Flow Blocker).
  if (!workspaceCtx || (alerts.loading && alerts.data === null)) return <Spinner label="Loading alerts" />;
  if (alerts.error) return <Alert variant="error">{alerts.error}</Alert>;

  const showEmptyState = noFiltersActive && heroCounts.unread === 0 && openRows.length === 0 && !alerts.error;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title={aggregate ? 'Alerts — all my workspaces' : 'Alerts'}
        // Names the clients in aggregate mode. "3 workspaces" is a
        // number a consultant then has to go and resolve; the names are
        // the thing that tells them whether the sweep they are about to
        // do covers who they think it covers.
        subtitle={
          aggregate
            ? `${heroCounts.unread} unread · ${isTruncated ? `${rows.length} of ${serverTotal}` : `${serverTotal}`} total · ${
                clients.length > 0 ? clients.map((c) => c.name).join(' · ') : 'no clients with alerts'
              }`
            : `${heroCounts.unread} unread · ${isTruncated ? `${rows.length} of ${serverTotal}` : `${serverTotal}`} total`
        }
        freshness={{
          updatedAt: feedUpdatedAt ? new Date(feedUpdatedAt).toISOString() : new Date().toISOString(),
          source: 'Legislative + case-law + breach-intel scanners',
          reviewer: roster,
        }}
        right={
          <>
            {canAggregate ? (
              <Button
                variant="secondary"
                onClick={() => {
                  // Clearing `client` on the way out of aggregate mode
                  // matters: a client filter is meaningless in a
                  // single-workspace inbox and would silently empty it.
                  setSearchParams(
                    (prev) => {
                      const params = new URLSearchParams(prev);
                      if (aggregate) {
                        params.delete('view');
                        params.delete('client');
                      } else {
                        params.set('view', 'aggregate');
                      }
                      return params;
                    },
                    { replace: true }
                  );
                  clearSelection();
                }}
              >
                {aggregate
                  ? `Just ${workspaceCtx.workspace.name}`
                  : `All ${teamCount} clients`}
              </Button>
            ) : null}
            <Link
              to={`${prefix}/alerts/preferences`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '6px var(--space-3)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--surface)',
                color: 'var(--text)',
                fontSize: 'var(--font-size-2xs)',
                fontWeight: 500,
                textDecoration: 'none',
                minHeight: 'var(--min-touch-target)',
              }}
            >
              Preferences
            </Link>
          </>
        }
      />

      {bulkError ? <Alert variant="error" onDismiss={() => setBulkError(null)}>{bulkError}</Alert> : null}
      {bulkNotice ? <Alert variant="warning" onDismiss={() => setBulkNotice(null)}>{bulkNotice}</Alert> : null}

      {showEmptyState ? (
        <AlertsEmpty
          prefix={prefix}
          subscription={prefs.data?.preferences ?? null}
          aggregate={aggregate}
          workspaceCount={teamCount}
        />
      ) : (
        <>
          <div
            role="group"
            aria-label="Alert summary"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-3)' }}
          >
            <HeroTile
              label="Unread"
              value={heroCounts.unread}
              sub="awaiting triage"
              onClick={() => setParam('unread', '1')}
            />
            <HeroTile
              label="Critical"
              value={heroCounts.critical}
              sub="need action now"
              tone="critical"
              onClick={() => setParam('severity', 'critical')}
            />
            <HeroTile
              label={`Deadlines (${DEADLINE_WINDOW_DAYS}d)`}
              value={heroCounts.deadlines}
              sub={`next ${DEADLINE_WINDOW_DAYS} days`}
              tone="warning"
              onClick={() => setParam('deadline', '30d')}
            />
            <HeroTile
              label="Actioned"
              value={heroCounts.done}
              sub="converted or closed"
              onClick={() => setParam('status', 'done')}
            />
          </div>

          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-card)',
              background: 'var(--surface)',
              padding: 'var(--space-4)',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--space-4)',
              alignItems: 'flex-end',
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
              {aggregate ? (
                <FilterField id="alerts-client" label="Client">
                  <Select
                    value={clientFilter}
                    onChange={(e) => setParam('client', e.target.value === 'all' ? null : e.target.value)}
                  >
                    <option value="all">All clients</option>
                    {clients.map((c) => (
                      <option key={c.slug} value={c.slug}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </FilterField>
              ) : null}
              <FilterField id="alerts-status" label="Status">
                <Select value={statusFilter} onChange={(e) => setParam('status', e.target.value === 'all' ? 'all' : e.target.value)}>
                  <option value="open">Open</option>
                  <option value="all">All</option>
                  <option value="done">Actioned (converted/closed)</option>
                  <option value="converted">Converted</option>
                  <option value="closed">Closed</option>
                </Select>
              </FilterField>
            </div>
            <span aria-hidden="true" style={{ width: '1px', alignSelf: 'stretch', background: 'var(--border)' }} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
              <FilterField id="alerts-category" label="Category">
                <Select value={categoryFilter} onChange={(e) => setParam('category', e.target.value === 'all' ? null : e.target.value)}>
                  <option value="all">All</option>
                  {CATEGORY_VALUES.map((c) => (
                    <option key={c} value={c}>
                      {alertCategoryLabel(c)}
                    </option>
                  ))}
                </Select>
              </FilterField>
              <FilterField id="alerts-severity" label="Severity">
                <Select value={severityFilter} onChange={(e) => setParam('severity', e.target.value === 'all' ? null : e.target.value)}>
                  <option value="all">All</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="elevated">Elevated</option>
                  <option value="moderate">Moderate</option>
                  <option value="info">Info</option>
                </Select>
              </FilterField>
            </div>
            <span aria-hidden="true" style={{ width: '1px', alignSelf: 'stretch', background: 'var(--border)' }} />
            <button
              type="button"
              aria-pressed={unreadOnly}
              onClick={() => setParam('unread', unreadOnly ? null : '1')}
              style={{
                appearance: 'none',
                padding: '6px var(--space-3)',
                border: `1px solid ${unreadOnly ? 'var(--accent)' : 'var(--border)'}`,
                background: unreadOnly ? 'var(--accent)' : 'transparent',
                color: unreadOnly ? 'var(--text-inverse)' : 'var(--text-muted)',
                borderRadius: '999px',
                fontSize: 'var(--font-size-3xs)',
                fontWeight: unreadOnly ? 700 : 500,
                cursor: 'pointer',
                minHeight: 'var(--min-touch-target)',
              }}
            >
              Unread only
            </button>
          </div>

          {rollups.length > 0 ? (
            <div role="group" aria-label="Signals affecting more than one client" style={{ display: 'grid', gap: 'var(--space-3)' }}>
              {rollups.map((g) => (
                <AggregateRollup
                  key={g.key}
                  group={g}
                  canConvert={canConvertAnywhere}
                  canConvertMember={canConvertRow}
                  busy={bulkBusy}
                  onConvertAll={(ids) => {
                    setPriorSelection(new Set(selected));
                    setSelected(new Set(ids));
                    openConfirm('convert');
                  }}
                  onFocusGroup={() => {
                    setSelected(new Set(
                      g.members
                        .filter((m) => m.status === 'open' && canConvertRow(m))
                        .map((m) => m.alert_id)
                    ));
                  }}
                />
              ))}
            </div>
          ) : null}

          <ul
            ref={listRef}
            tabIndex={-1}
            aria-label={aggregate ? 'Alerts across all my workspaces' : 'Alerts'}
            style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-2)', outline: 'none' }}
          >
            {filtered.map((a) => (
              <AlertRow
                key={a.alert_id}
                alert={a}
                prefix={prefix}
                aggregate={aggregate}
                returnSearch={aggregate ? searchParams.toString() : undefined}
                selected={selected.has(a.alert_id)}
                onToggle={() => toggleSelected(a.alert_id)}
                selectable={canConvertRow(a)}
              />
            ))}
            {filtered.length === 0 ? (
              <li
                style={{
                  padding: 'var(--space-6)',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  border: '1px dashed var(--border-strong)',
                  borderRadius: 'var(--radius-card)',
                }}
              >
                No alerts match the current filters.
              </li>
            ) : null}
          </ul>

          {selected.size > 0 && canConvertAnywhere ? (
            <div
              style={{
                position: 'sticky',
                bottom: 'calc(var(--space-4) + var(--mobile-tabbar-height, 0px) + env(safe-area-inset-bottom, 0px))',
                border: '1px solid var(--border-strong)',
                borderRadius: 'var(--radius-card)',
                background: 'var(--surface-elevated)',
                boxShadow: 'var(--shadow-lg)',
                padding: 'var(--space-3) var(--space-4)',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 'var(--space-3)',
                zIndex: 60,
              }}
            >
              <span style={{ color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
                {selected.size} selected
                {aggregate && selectedWorkspaceCount > 0
                  ? ` · ${selectedWorkspaceNames.join(', ')}`
                  : ''}
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                <Button variant="ghost" onClick={clearSelection} disabled={bulkBusy}>
                  Clear
                </Button>
                <Button variant="secondary" onClick={openSnooze} disabled={bulkBusy}>
                  Snooze
                </Button>
                <Button variant="secondary" onClick={() => openConfirm('dismiss')} disabled={bulkBusy}>
                  Dismiss
                </Button>
                <Button variant="primary" onClick={() => openConfirm('convert')} disabled={bulkBusy}>
                  Convert {selected.size} to items →
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}

      <SnoozeDialog
        open={snoozeOpen}
        onClose={() => setSnoozeOpen(false)}
        onConfirm={runBulkSnooze}
        busy={bulkBusy}
        error={bulkError}
      />
      <ConfirmDialog
        open={confirmKind === 'convert'}
        // Names the client(s) whenever we're in aggregate mode — not only
        // when the selection spans several. A single-client selection
        // inside a combined inbox is exactly the case where the user has
        // the least context about whose register is about to change
        // (ux-adversary Nadia Blocker).
        title={
          aggregate && selectedWorkspaceCount >= 1
            ? `Convert ${selected.size} alert${selected.size === 1 ? '' : 's'} in ${selectedWorkspaceNames.join(', ')}?`
            : `Convert ${selected.size} alert${selected.size === 1 ? '' : 's'} to register items?`
        }
        description={
          aggregate && selectedWorkspaceCount >= 1 ? (
            <>
              {/* Names the clients being written to. A cross-tenant write
                  confirmed by a bare count is the one place a consultant
                  can't check their own work — "3 alerts" doesn't say
                  whose register is about to change. */}
              Each alert links its matched register items in its own client's workspace and marks
              that alert converted. Anywhere you aren't an editor is skipped and reported, not
              written.
            </>
          ) : (
            "Each alert links its matched register items and marks the alert converted. Alerts with no matching items are refused, not skipped silently — you'll see a count."
          )
        }
        confirmLabel={`Convert ${selected.size} →`}
        onConfirm={runBulkConvert}
        onClose={() => {
          setConfirmKind(null);
          if (priorSelection) {
            setSelected(priorSelection);
            setPriorSelection(null);
          }
        }}
        busy={bulkBusy}
        error={bulkError}
      />
      <ConfirmDialog
        open={confirmKind === 'dismiss'}
        title={
          aggregate && selectedWorkspaceCount >= 1
            ? `Dismiss ${selected.size} alert${selected.size === 1 ? '' : 's'} in ${selectedWorkspaceNames.join(', ')}?`
            : `Dismiss ${selected.size} alert${selected.size === 1 ? '' : 's'}?`
        }
        description={
          aggregate && selectedWorkspaceCount >= 1
            ? `Moves to Closed in each client's workspace. Anywhere you aren't an editor is skipped and reported.`
            : "Dismissed alerts move to Closed. This doesn't affect any linked register items."
        }
        confirmLabel="Dismiss"
        onConfirm={runBulkDismiss}
        onClose={() => setConfirmKind(null)}
        busy={bulkBusy}
        error={bulkError}
        danger
      />
    </div>
  );
}

function FilterField({ id, label, children }: { id: string; label: string; children: React.ReactElement }) {
  return (
    <div style={{ minWidth: '160px' }}>
      <label htmlFor={id} style={{ display: 'block', fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', marginBottom: '4px' }}>
        {label}
      </label>
      {React.cloneElement(children, { id })}
    </div>
  );
}

function HeroTile({
  label,
  value,
  sub,
  tone,
  onClick,
}: {
  label: string;
  value: number;
  sub: string;
  tone?: 'critical' | 'warning';
  onClick?: () => void;
}) {
  const color = tone === 'critical' ? 'var(--critical)' : tone === 'warning' ? 'var(--warning)' : 'var(--text)';
  const bg = tone === 'critical' ? 'var(--danger-bg)' : tone === 'warning' ? 'var(--warning-bg)' : 'var(--surface)';
  const borderColor = tone === 'critical' ? 'var(--critical)' : tone === 'warning' ? 'var(--warning)' : 'var(--border)';
  const content = (
    <>
      <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div style={{ fontSize: 'var(--font-size-2xl)', color, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{value}</div>
      <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>{sub}</div>
    </>
  );
  const style: React.CSSProperties = {
    border: `1px solid ${borderColor}`,
    borderRadius: 'var(--radius-card)',
    background: bg,
    padding: 'var(--space-3) var(--space-4)',
    textAlign: 'left',
    cursor: onClick ? 'pointer' : 'default',
  };
  if (onClick) {
    return (
      <button type="button" onClick={onClick} style={{ ...style, minHeight: 'var(--min-touch-target)' }}>
        {content}
      </button>
    );
  }
  return <div style={style}>{content}</div>;
}

function AlertRow({
  alert,
  prefix,
  aggregate,
  returnSearch,
  selected,
  onToggle,
  selectable,
}: {
  alert: AlertListItem;
  prefix: string;
  aggregate: boolean;
  returnSearch?: string;
  selected: boolean;
  onToggle: () => void;
  selectable: boolean;
}) {
  const unread = !alert.read_at;
  const domId = `alert-${alert.alert_id}`;
  const href = aggregate && alert.workspace_slug
    ? `/w/${alert.workspace_slug}/app/alerts/${alert.alert_id}?from=aggregate${returnSearch ? `&return=${encodeURIComponent(returnSearch)}` : ''}`
    : `${prefix}/alerts/${alert.alert_id}`;
  return (
    <li
      aria-labelledby={`${domId}-t`}
      aria-describedby={`${domId}-meta`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: selected ? 'var(--row-selected)' : 'var(--surface)',
      }}
    >
      {selectable ? (
        <Checkbox
          id={`${domId}-select`}
          label={<span className="sr-only">Select {alert.title}</span>}
          checked={selected}
          onChange={onToggle}
        />
      ) : null}
      <CategoryIcon category={alert.category} />
      <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {aggregate ? (
            <WorkspaceTag
              name={alert.workspace_name ?? 'Unknown workspace'}
              slug={alert.workspace_slug}
            />
          ) : null}
          <SeverityBadge severity={alert.severity === 'info' ? 'standard' : alert.severity} />
          <Link
            to={href}
            id={`${domId}-t`}
            style={{
              color: 'var(--text)',
              fontWeight: unread ? 700 : 500,
              textDecoration: 'none',
            }}
          >
            {unread ? <span className="sr-only">Unread. </span> : null}
            {alert.title}
          </Link>
          {unread ? (
            <span aria-hidden="true" style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--accent)' }} />
          ) : null}
        </div>
        <span id={`${domId}-meta`} style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
          {alertCategoryLabel(alert.category)}
          {(() => {
            const n = refCount(alert.register_item_refs);
            return n > 0 ? ` · ${n} item${n === 1 ? '' : 's'}` : '';
          })()}
          {alert.deadline_at ? ` · Deadline ${fmtDate(alert.deadline_at)}` : ''}
          {' · '}
          {fmtDate(alert.created_at)}
          {' · '}
          {alert.status}
        </span>
      </div>
    </li>
  );
}
